import { useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Plug, Search, Filter, Check, AlertCircle, ShieldCheck, Eye, Sparkles, ArrowRight,
} from "lucide-react";
import {
  AGENTIC_INTEGRATIONS, INTEGRATION_GROUPS, AUTH_LABEL,
  type AgenticIntegration, type IntegrationGroup, type AuthMethod,
} from "../data/agenticIntegrations";
import { BrandIcon, type BrandSlug } from "../components/BrandIcon";
import { getConnection, type ConnectionStatus } from "../lib/connectionStore";
import { ConnectAppSheet } from "../components/admin/ConnectAppSheet";
import { getSession } from "../lib/session";
import { cls } from "../lib/cls";

/* All-connections management surface.
   Lists every connector, filterable by group + status + auth method.
   Admin-gated for connect/disconnect; non-admins see read-only state. */

const STATUS_TONE: Record<ConnectionStatus, string> = {
  connected:    "bg-fern/15 border-fern/40 text-fern",
  connecting:   "bg-amber-50 border-amber-200 text-amber-700",
  error:        "bg-foyr/10 border-foyr/30 text-foyr-deep",
  disconnected: "bg-cream-dark border-border text-ink-500",
};

const STATUS_DOT: Record<ConnectionStatus, string> = {
  connected:    "bg-fern",
  connecting:   "bg-amber-500 animate-pulse",
  error:        "bg-foyr",
  disconnected: "bg-ink-300",
};

const AUTH_TONE: Record<AuthMethod, string> = {
  oauth2:          "bg-sky-50 border-sky-200 text-sky-700",
  api_key:         "bg-violet-50 border-violet-200 text-violet-700",
  basic_auth:      "bg-slate-100 border-slate-300 text-slate-700",
  service_account: "bg-amber-50 border-amber-200 text-amber-700",
  webhook:         "bg-rose-50 border-rose-200 text-rose-700",
};

export default function Connections(): JSX.Element {
  const isAdmin = getSession()?.isAdmin === true;
  const [activeApp, setActiveApp] = useState<AgenticIntegration | null>(null);
  const [refreshSignal, setRefreshSignal] = useState(0);
  const [q, setQ] = useState("");
  const [group, setGroup] = useState<IntegrationGroup | "all">("all");
  const [statusFilter, setStatusFilter] = useState<ConnectionStatus | "all">("all");

  const onPick = useCallback((a: AgenticIntegration) => {
    if (!isAdmin) return;
    setActiveApp(a);
  }, [isAdmin]);

  const rows = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return AGENTIC_INTEGRATIONS.filter((a) => {
      if (group !== "all" && a.group !== group) return false;
      if (statusFilter !== "all") {
        const s = getConnection(a.name).status;
        if (s !== statusFilter) return false;
      }
      if (ql && !`${a.name} ${a.group} ${a.agenticAction}`.toLowerCase().includes(ql)) return false;
      return true;
    });
  }, [q, group, statusFilter, refreshSignal]);

  const counts = useMemo(() => {
    let connected = 0, connecting = 0, error = 0, disconnected = 0;
    for (const a of AGENTIC_INTEGRATIONS) {
      const s = getConnection(a.name).status;
      if (s === "connected") connected++;
      else if (s === "connecting") connecting++;
      else if (s === "error") error++;
      else disconnected++;
    }
    return { connected, connecting, error, disconnected };
  }, [refreshSignal]);

  return (
    <div className="max-w-[1280px] mx-auto px-4 sm:px-8 py-10 space-y-6">
      <header className="relative rounded-3xl border border-border/70 bg-gradient-to-br from-foyr/10 via-cream to-sea/5 px-6 py-7 overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.04),0_12px_36px_-18px_rgba(0,0,0,0.10)]">
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-foyr/10 blur-3xl -translate-y-1/3 translate-x-1/3 pointer-events-none" />
        <div className="relative">
          <div className="eyebrow inline-flex items-center gap-1.5">
            <Plug className="w-3 h-3" /> Connections · per studio · per user
          </div>
          <h1 className="display-1 mt-1 text-ink-950 leading-[1.04]">
            Your studio's tools, all in one place.
          </h1>
          <p className="text-[14px] text-ink-700 mt-2 max-w-2xl leading-relaxed">
            Every connection is encrypted at rest and scoped to your studio.
            {isAdmin ? " Click any app to connect or disconnect." : " View-only — admin rights are required to change connections."}
          </p>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <KPI label="Connected"    value={counts.connected}    tone="fern"     icon={<Check className="w-3.5 h-3.5" />} />
        <KPI label="Connecting"   value={counts.connecting}   tone="amber"    icon={<Sparkles className="w-3.5 h-3.5" />} />
        <KPI label="Error"        value={counts.error}        tone="foyr"     icon={<AlertCircle className="w-3.5 h-3.5" />} />
        <KPI label="Disconnected" value={counts.disconnected} tone="slate"    icon={<Plug className="w-3.5 h-3.5" />} />
      </div>

      <div className="rounded-2xl border border-border/70 bg-cream p-3 space-y-2.5 shadow-sm">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, category, or what the agent does…"
              className="w-full bg-cream-dark/40 border border-border rounded-lg pl-8 pr-3 py-2 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-foyr/30"
            />
          </div>
          <span className="pill bg-cream-dark border border-border text-ink-700 text-[10.5px] tabular">
            {rows.length} / {AGENTIC_INTEGRATIONS.length}
          </span>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10.5px] uppercase tracking-wider text-ink-500 inline-flex items-center gap-1">
            <Filter className="w-3 h-3" /> Group
          </span>
          <button onClick={() => setGroup("all")} className={cls("pill text-[10.5px] border", group === "all" ? "bg-ink-950 text-cream border-ink-950" : "bg-cream-dark border-border text-ink-700 hover:border-foyr/40")}>All</button>
          {INTEGRATION_GROUPS.map((g) => (
            <button
              key={g}
              onClick={() => setGroup(g)}
              className={cls("pill text-[10.5px] border", group === g ? "bg-ink-950 text-cream border-ink-950" : "bg-cream-dark border-border text-ink-700 hover:border-foyr/40")}
            >
              {g}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10.5px] uppercase tracking-wider text-ink-500">Status</span>
          {(["all","connected","connecting","error","disconnected"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={cls("pill text-[10.5px] border", statusFilter === s ? "bg-ink-950 text-cream border-ink-950" : "bg-cream-dark border-border text-ink-700 hover:border-foyr/40")}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {rows.map((a, i) => {
          const conn = getConnection(a.name);
          void refreshSignal;
          return (
            <motion.button
              key={a.name}
              onClick={() => onPick(a)}
              disabled={!isAdmin}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.012, duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className={cls(
                "text-left rounded-2xl border border-border/70 bg-cream p-3 shadow-sm hover:shadow-[0_4px_12px_-4px_rgba(0,0,0,0.10)] hover:-translate-y-0.5 transition-all",
                !isAdmin && "cursor-default hover:translate-y-0",
              )}
              title={isAdmin ? "Click to connect / disconnect" : "Admin rights required"}
            >
              <div className="flex items-start gap-2.5">
                <div className="grid place-items-center w-10 h-10 rounded-xl border border-border/70 bg-cream shrink-0 shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
                  <BrandIcon
                    slug={a.brand as BrandSlug}
                    group={a.group}
                    size={22}
                    title={a.name}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-1.5">
                    <span className="font-semibold text-ink-950 text-[13px] truncate">{a.name}</span>
                    <span className={cls("inline-flex items-center gap-1 pill text-[9.5px] border", STATUS_TONE[conn.status])}>
                      <span className={cls("w-1.5 h-1.5 rounded-full", STATUS_DOT[conn.status])} />
                      {conn.status}
                    </span>
                  </div>
                  <div className="text-[10px] text-ink-500 uppercase tracking-wider">{a.group}</div>
                  <div className="mt-1 inline-flex items-center gap-1 flex-wrap">
                    <span className={cls("pill text-[9.5px] border", AUTH_TONE[a.auth])}>
                      {AUTH_LABEL[a.auth]}
                    </span>
                    {conn.account ? (
                      <span className="text-[10px] text-ink-700 tabular truncate max-w-[160px]">{conn.account}</span>
                    ) : null}
                  </div>
                  <div className="text-[11px] text-ink-700 leading-snug mt-1.5 inline-flex items-start gap-1">
                    <ArrowRight className="w-3 h-3 text-foyr mt-0.5 shrink-0" />
                    <span>{a.agenticAction}</span>
                  </div>
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>

      {!isAdmin ? (
        <div className="rounded-xl border border-mulberry/30 bg-mulberry/5 px-3 py-2.5 flex items-center gap-2 flex-wrap">
          <ShieldCheck className="w-3.5 h-3.5 text-mulberry" />
          <span className="text-[12px] text-ink-950 font-medium">View-only · admin rights required to change connections.</span>
          <Eye className="w-3.5 h-3.5 text-ink-500 ml-auto" />
        </div>
      ) : null}

      <ConnectAppSheet
        app={activeApp}
        onClose={() => setActiveApp(null)}
        onChanged={() => setRefreshSignal((n) => n + 1)}
      />
    </div>
  );
}

function KPI({ label, value, tone, icon }: { label: string; value: number; tone: "fern" | "amber" | "foyr" | "slate"; icon: JSX.Element }): JSX.Element {
  const cardTone: Record<typeof tone, string> = {
    fern:  "border-fern/40 bg-fern/5    text-fern",
    amber: "border-amber-300 bg-amber-50 text-amber-700",
    foyr:  "border-foyr/40 bg-foyr/5    text-foyr-deep",
    slate: "border-border    bg-cream     text-ink-500",
  };
  return (
    <div className={cls("rounded-xl border p-3 shadow-sm", cardTone[tone])}>
      <div className="flex items-center gap-1.5">
        {icon}
        <span className="text-[10.5px] uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-2xl font-semibold tabular mt-0.5 text-ink-950">{value}</div>
    </div>
  );
}
